import { pxtorem } from "@/utils/pxtorem";
import { ProductDetailModel } from "@akc/biz-api";
import { Image } from "antd-mobile";
import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.less";

type Props = {
  productDetail?: ProductDetailModel;
  xScroll?: boolean; // 允许横向滚动，默认true
  yScroll?: boolean; // 允许纵向滚动，默认true
  onVideoCardClick?: () => void; // 点击视频卡片的回调
};

/**
 * 视频大卡浮动入口组件
 */
const FloatVideoCardComp: React.FC<Props> = ({
  productDetail,
  xScroll = true,
  yScroll = true,
  onVideoCardClick,
}) => {
  // 边距和卡片尺寸 - 使用CSS变量在样式中定义，确保响应式效果
  const EDGE_MARGIN = 12; // 像素边距，在不同设备上通过CSS实现响应式
  const CARD_SIZE = 50; // 卡片尺寸

  // 记录当前位置
  const [position, setPosition] = useState({
    x: 0, // 初始值会在useEffect中设置
    y: 240, // 初始位置 top: 240px
  });

  // 记录拖拽初始点
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  // 记录当前是在左侧还是右侧
  const [isAtRight, setIsAtRight] = useState(true); // 默认在右侧

  // 组件DOM引用
  const cardRef = useRef<HTMLDivElement>(null);
  // 记录屏幕尺寸
  const screenWidth = useRef(document.body.clientWidth);
  const screenHeight = useRef(window.innerHeight);

  // 初始化位置和处理屏幕大小变化
  useEffect(() => {
    const updatePosition = () => {
      screenWidth.current = document.body.clientWidth;
      screenHeight.current = window.innerHeight;

      // 根据当前是左侧还是右侧来设置位置
      if (isAtRight) {
        setPosition(prev => ({
          ...prev,
          x: screenWidth.current - (CARD_SIZE + EDGE_MARGIN),
        }));
      } else {
        setPosition(prev => ({
          ...prev,
          x: EDGE_MARGIN,
        }));
      }
    };

    // 初始调用一次，设置初始位置
    updatePosition();

    // 监听屏幕尺寸变化
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, [CARD_SIZE, EDGE_MARGIN, isAtRight]);

  // 开始拖拽
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!xScroll && !yScroll) {
      return;
    }

    const touch = e.touches[0];
    setStartPoint({
      x: touch.clientX,
      y: touch.clientY,
    });
    setIsDragging(true);
  };

  // 拖拽中
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) {
      return;
    }
    e?.preventDefault && e?.preventDefault(); // 阻止默认行为，防止页面滚动

    const touch = e.touches[0];
    const offsetX = touch.clientX - startPoint.x;
    const offsetY = touch.clientY - startPoint.y;

    // 根据xScroll和yScroll的值来决定是否可以在对应方向上移动
    let newX = position.x;
    let newY = position.y;

    if (xScroll) {
      newX += offsetX;
      // 限制不超出屏幕
      newX = Math.max(0, Math.min(newX, screenWidth.current - CARD_SIZE));
    }

    if (yScroll) {
      newY += offsetY;
      // 限制不超出屏幕
      newY = Math.max(0, Math.min(newY, screenHeight.current - CARD_SIZE));
    }

    setPosition({ x: newX, y: newY });
    setStartPoint({
      x: touch.clientX,
      y: touch.clientY,
    });
  };

  // 结束拖拽
  const handleTouchEnd = () => {
    if (!isDragging) {
      return;
    }
    setIsDragging(false);

    // 如果xScroll为true，拖拽结束后需要吸附到左侧或右侧
    if (xScroll) {
      // 判断是吸附到左侧还是右侧
      const halfScreenWidth = screenWidth.current / 2;
      const shouldBeAtRight = position.x >= halfScreenWidth;

      setIsAtRight(shouldBeAtRight);

      const newX = shouldBeAtRight
        ? screenWidth.current - (CARD_SIZE + EDGE_MARGIN) // 吸附到右侧
        : EDGE_MARGIN; // 吸附到左侧

      setPosition({
        ...position,
        x: newX,
      });
    }
  };

  // 处理点击事件
  const handleClick = () => {
    if (onVideoCardClick) {
      onVideoCardClick();
    }
  };

  // 如果没有视频数据则不显示组件
  if (!productDetail?.isShowProductVideo) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      className={`${styles.floatVideoCard} ${isDragging ? styles.dragging : ""}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${CARD_SIZE}px`,
        height: `${CARD_SIZE}px`,
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}>
      <Image
        className={styles.videoCardIcon}
        src="https://akim-oss.aikucun.com/caa4ee9b0afac3cac9c3d64b491c342b79eb2baf_1753845437710_51.png"
        alt="视频卡片"
      />
    </div>
  );
};

export default FloatVideoCardComp;
